import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, ChevronLeft, ChevronRight, Play } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { portfolioItems } from '../data/mockData';
import { useLanguage } from '../i18n/LanguageContext';

const formatCount = (value) => String(value).padStart(2, '0');
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const resultKeyMap = {
  joaquin: 'portfolio.items.joaquin_result',
  edt: 'portfolio.items.edt_result',
  indawork: 'portfolio.items.indawork_result',
  luma: 'portfolio.items.luma_result',
  rolitornillos: 'portfolio.items.rolitornillos_result',
  kukymotos: 'portfolio.items.kukymotos_result',
  cliente_adicional: 'portfolio.items.otros_result',
};

const titleKeyMap = {
  cliente_adicional: 'portfolio.items.otros_title',
};

const PortfolioSection = () => {
  const { t } = useLanguage();
  const [activeIndex, setActiveIndex] = useState(0);
  const trackRef = useRef(null);
  const [trackMetrics, setTrackMetrics] = useState({ cardWidth: 304, gap: 24 });

  const displayItems = useMemo(() => {
    const items = [];
    portfolioItems.forEach((item) => {
      if (Array.isArray(item.instagramReels) && item.instagramReels.length) {
        item.instagramReels.forEach((reel, i) => {
          items.push({
            ...item,
            id: `${item.id}-${i}`,
            thumbnail: reel.thumbnailUrl || item.thumbnail,
            instagramReels: [reel],
          });
        });
      } else {
        items.push(item);
      }
    });
    return items;
  }, [portfolioItems]);

  const totalItems = displayItems.length;

  if (totalItems === 0) {
    return null;
  }

  const activeItem = displayItems[activeIndex];
  const maxIndex = totalItems - 1;

  const goToIndex = (index) => {
    setActiveIndex(clamp(index, 0, maxIndex));
  };

  const goToPrevious = () => {
    if (activeIndex === 0) {
      return;
    }

    goToIndex(activeIndex - 1);
  };

  const goToNext = () => {
    if (activeIndex === maxIndex) {
      return;
    }

    goToIndex(activeIndex + 1);
  };

  useEffect(() => {
    const updateMetrics = () => {
      const trackElement = trackRef.current;

      if (!(trackElement instanceof HTMLElement)) {
        return;
      }

      const firstCard = trackElement.querySelector('[data-portfolio-card]');

      if (!(firstCard instanceof HTMLElement)) {
        return;
      }

      const styles = window.getComputedStyle(trackElement);
      const rawGap = Number.parseFloat(styles.columnGap || styles.gap || '24');

      setTrackMetrics({
        cardWidth: firstCard.getBoundingClientRect().width,
        gap: Number.isFinite(rawGap) ? rawGap : 24,
      });
    };

    updateMetrics();

    let resizeObserver = null;

    if (typeof window.ResizeObserver === 'function' && trackRef.current instanceof HTMLElement) {
      resizeObserver = new window.ResizeObserver(updateMetrics);
      resizeObserver.observe(trackRef.current);
    }

    window.addEventListener('resize', updateMetrics);

    return () => {
      window.removeEventListener('resize', updateMetrics);

      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const portfolioSchema = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Portafolio de edicion de video',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: displayItems.length,
      itemListElement: displayItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.title,
        description: `${item.summary || ''}. ${item.result || ''}`,
        url: item.href || '',
      })),
    }),
    [displayItems],
  );

  // Dialog state for deferred media rendering (iframe or <video>)
  const [activeMedia, setActiveMedia] = useState(null);

  const handleDialogOpenChange = (open) => {
    // when the dialog closes, clear the active media to unmount and free memory
    if (!open) {
      setActiveMedia(null);
    }
  };

  // Normalize reel entries (support strings or objects). Returns { url, thumbnailUrl, kind }
  const normalizeReel = (reel) => {
    if (!reel) return { url: null, thumbnailUrl: null, kind: null };

    if (typeof reel === 'string') {
      const raw = reel.trim();
      // try to detect Instagram post/reel links and convert to embed URL
      try {
        const base = typeof window !== 'undefined' && window.location ? window.location.href : 'http://localhost';
        const maybeUrl = raw.startsWith('http') ? new URL(raw) : new URL(raw, base);
        const pathname = maybeUrl.pathname.replace(/\/+$/, '');

        if (/\/(reel|p|tv)\//i.test(pathname) || raw.includes('instagram.com') && raw.includes('/embed')) {
          // ensure embed path
          let embedPath = pathname;
          if (!/\/embed$/.test(embedPath)) embedPath = `${embedPath}/embed`;
          const embedUrl = `${maybeUrl.origin}${embedPath}`;
          return { url: embedUrl, thumbnailUrl: null, kind: 'instagram' };
        }

        // direct video file (mp4/webm)
        if (/\.(mp4|webm|ogg)(\?.*)?$/i.test(raw)) {
          return { url: raw, thumbnailUrl: null, kind: 'video' };
        }

        // YouTube Shorts / watch URL
        const youtubeMatch = raw.match(/(?:youtube\.com\/(?:shorts\/|watch\?v=)|youtu\.be\/)([\w-]{11})/);
        if (youtubeMatch) {
          return { url: `https://www.youtube.com/embed/${youtubeMatch[1]}?controls=1&rel=0`, thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`, kind: 'youtube', originalUrl: raw };
        }

        // fallback: external URL
        return { url: raw, thumbnailUrl: null, kind: 'external' };
      } catch (e) {
        return { url: raw, thumbnailUrl: null, kind: 'external' };
      }
    }

    // object form
    const videoUrl = reel.videoUrl || reel.url || reel.src || '';
    const thumb = reel.thumbnailUrl || reel.poster || null;

    // YouTube detection (Shorts & watch URLs)
    const youtubeMatch = videoUrl.match(/(?:youtube\.com\/(?:shorts\/|watch\?v=)|youtu\.be\/)([\w-]{11})/);
    if (youtubeMatch) {
      return { url: `https://www.youtube.com/embed/${youtubeMatch[1]}?controls=1&rel=0`, thumbnailUrl: `https://img.youtube.com/vi/${youtubeMatch[1]}/maxresdefault.jpg`, kind: 'youtube', originalUrl: videoUrl };
    }

    if (videoUrl.includes('instagram.com')) {
      try {
        const url = new URL(videoUrl);
        let pathname = url.pathname.replace(/\/+$/, '');
        if (/\/(reel|p|tv)\//i.test(pathname)) {
          if (!/\/embed$/.test(pathname)) {
            pathname = `${pathname}/embed`;
          }
          return { url: `${url.origin}${pathname}`, thumbnailUrl: thumb, kind: 'instagram' };
        }
      } catch (e) {
        // fall through
      }
    }

    const kind = /\.(mp4|webm|ogg)/i.test(videoUrl) ? 'video' : 'external';
    return { url: videoUrl, thumbnailUrl: thumb, kind };
  };

  // Ensure the exact CamelCase attribute `allowTransparency` exists on the iframe
  // without passing a non-standard prop to React (avoids React warnings).
  useEffect(() => {
    if (!activeMedia) return;

    const timer = setTimeout(() => {
      const iframe = document.querySelector('iframe[title="Instagram Reel"]');
      if (iframe) {
        try {
          iframe.setAttribute('allowTransparency', 'true');
        } catch (e) {
          // ignore
        }
      }
    }, 60);

    return () => clearTimeout(timer);
  }, [activeMedia]);

  const trackTranslate = `translate3d(calc(50% - ${trackMetrics.cardWidth / 2}px - ${activeIndex} * ${trackMetrics.cardWidth + trackMetrics.gap}px), 0, 0)`;
  const glassPillClass = 'rounded-full border border-white/90 bg-[linear-gradient(135deg,rgba(255,255,255,0.8)_0%,rgba(255,255,255,0.4)_100%)] shadow-[0_8px_32px_-4px_rgba(0,0,0,0.04),0_4px_16px_-4px_rgba(0,0,0,0.04),inset_0_1px_1px_rgba(255,255,255,0.8),inset_0_-1px_1px_rgba(255,255,255,0.3)]';

  return (
    <section
      id="portfolio"
      aria-labelledby="portfolio-title"
      className="relative overflow-hidden bg-[radial-gradient(130%_120%_at_50%_0%,#f8f9ff_0%,#edf0f9_60%,#e9edf6_100%)] py-24"
    >
      <div className="pointer-events-none absolute left-1/2 top-0 h-96 w-[140%] -translate-x-1/2 bg-[radial-gradient(circle_at_center,_rgba(255,255,255,0.78),_rgba(255,255,255,0.08)_65%,_rgba(255,255,255,0)_100%)]" />

      <div className="relative mx-auto max-w-[96rem] px-6">
        <header className="mx-auto max-w-3xl text-center">
          <span className="inline-block rounded-full bg-black/5 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-black/60">
            {t('portfolio.badge')}
          </span>
          <h2 id="portfolio-title" className="mt-5 text-4xl font-bold tracking-tight text-zinc-950 sm:text-5xl lg:text-6xl">
            {t('portfolio.title')}
          </h2>
        </header>

        <div className="relative mt-24">
          <div className="absolute -top-10 left-[6.6%] right-[6.6%] z-20 flex items-center justify-between md:-top-11">
            <a
              href={activeItem.href}
              target="_blank"
              rel="noreferrer"
              className={`inline-flex h-9 items-center gap-2 px-4 py-2 text-xs font-semibold text-black/70 transition-all duration-300 hover:-translate-y-0.5 hover:text-black sm:px-5 sm:text-sm ${glassPillClass}`}
            >
              {t('portfolio.viewYoutube')}
              <ArrowUpRight size={15} />
            </a>

            <div className={`flex h-12 items-baseline gap-0.5 px-3 text-black sm:px-5 ${glassPillClass}`}>
              <span className="text-[1.9rem] font-bold leading-none tracking-tight sm:text-[2.15rem]">
                {formatCount(activeIndex + 1)}
              </span>
              <span className="mb-0.5 text-xs font-semibold leading-none text-black/45 sm:text-sm">/{formatCount(totalItems)}</span>
            </div>
          </div>

          <div className="relative overflow-hidden rounded-[2.35rem] border border-white/35 bg-[linear-gradient(180deg,rgba(255,255,255,0.64)_0%,rgba(255,255,255,0.2)_100%)] px-2 pb-8 pt-16 shadow-[0_34px_95px_rgba(15,23,42,0.12)] sm:px-5 md:px-10">
            <div className="pointer-events-none absolute inset-x-[12%] top-[56%] h-36 -translate-y-1/2 rounded-full bg-zinc-950/30 blur-3xl sm:h-44" />

            <button
              type="button"
              onClick={goToPrevious}
              disabled={activeIndex === 0}
              aria-label="Video anterior"
              className={`absolute left-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/70 text-zinc-700 backdrop-blur-md transition-all duration-300 sm:left-4 md:h-12 md:w-12 ${
                activeIndex === 0 ? 'cursor-not-allowed opacity-35' : 'hover:scale-105 hover:text-zinc-950'
              }`}
            >
              <ChevronLeft size={22} />
            </button>

            <button
              type="button"
              onClick={goToNext}
              disabled={activeIndex === maxIndex}
              aria-label="Siguiente video"
              className={`absolute right-2 top-1/2 z-20 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/40 bg-white/70 text-zinc-700 backdrop-blur-md transition-all duration-300 sm:right-4 md:h-12 md:w-12 ${
                activeIndex === maxIndex ? 'cursor-not-allowed opacity-35' : 'hover:scale-105 hover:text-zinc-950'
              }`}
            >
              <ChevronRight size={22} />
            </button>

            <div className="overflow-hidden px-8 sm:px-14 md:px-16">
              <div
                ref={trackRef}
                className="flex items-end gap-4 transition-transform duration-500 sm:gap-6"
                style={{
                  transform: trackTranslate,
                  transitionTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
                }}
                role="list"
                aria-label="Videos del portafolio"
              >
                {displayItems.map((item, index) => {
                  const distance = index - activeIndex;
                  const absDistance = Math.abs(distance);
                  const isActive = absDistance === 0;
                  const opacity = absDistance > 2 ? 0 : absDistance === 2 ? 0.34 : absDistance === 1 ? 0.72 : 1;
                  const scale = isActive ? 1 : absDistance === 1 ? 0.92 : 0.86;
                  const translateY = isActive ? 0 : absDistance === 1 ? 26 : 42;
                  const norm = normalizeReel((item.instagramReels || [])[0]);
                  const baseId = item.id.split('-')[0];

                  return (
                    <article
                      key={item.id}
                      data-portfolio-card
                      role="listitem"
                      onClick={() => goToIndex(index)}
                      className="relative w-[15.4rem] shrink-0 cursor-pointer transition-all duration-500 sm:w-[17rem] md:w-[18.2rem] lg:w-[19rem]"
                      style={{
                        opacity,
                        transform: `translate3d(0, ${translateY}px, 0) scale(${scale})`,
                        filter: absDistance > 1 ? 'blur(0.5px)' : 'none',
                      }}
                      itemScope
                      itemType="https://schema.org/CreativeWork"
                    >
                      <div className="relative aspect-[9/16] overflow-hidden rounded-[2.1rem] border border-white/30 bg-zinc-950 shadow-[0_24px_70px_rgba(15,23,42,0.4)]">
                        {isActive && norm?.kind === 'youtube' ? (
                          <iframe
                            src={norm.url}
                            title="YouTube Shorts"
                            className="absolute inset-0 w-full h-full border-none"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                            allowFullScreen
                          />
                        ) : isActive && norm?.kind === 'instagram' ? (
                          <div className="absolute inset-0 overflow-hidden bg-zinc-950">
                            <iframe
                              src={norm.url}
                              title="Instagram Reel"
                              className="absolute left-0 top-0 w-full border-none"
                              style={{ height: '112%' }}
                              allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                              allowFullScreen
                              scrolling="no"
                            />
                          </div>
                        ) : (
                          <>
                            <img
                              src={item.instagramReels?.[0]?.thumbnailUrl || item.thumbnail}
                              alt={`Miniatura del video ${item.title}`}
                              loading={index <= 1 ? 'eager' : 'lazy'}
                              decoding="async"
                              fetchPriority={index === 0 ? 'high' : 'auto'}
                              className="h-full w-full object-cover"
                              width="720"
                              height="1280"
                              onError={(e) => {
                                try {
                                  const src = e.currentTarget.src;
                                  if (src.includes('maxresdefault')) {
                                    e.currentTarget.src = src.replace('maxresdefault', 'hqdefault');
                                  } else {
                                    e.currentTarget.onerror = null;
                                    e.currentTarget.src = '/placeholder.svg';
                                  }
                                } catch (err) {}
                              }}
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                            <div className="relative flex h-full flex-col justify-between p-4 sm:p-5">
                              <div>
                                <span className="inline-flex rounded-full bg-black/35 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-white/80">
                                  {item.category === 'Cliente' ? t('portfolio.category') : item.category}
                                </span>
                                <h3 className="mt-3 text-xl font-semibold leading-tight text-white sm:text-2xl" itemProp="name">
                                  {titleKeyMap[baseId] ? t(titleKeyMap[baseId]) : item.title}
                                </h3>
                              </div>

                              <div className="space-y-4">
                                <div className="flex justify-center">
                                  <button
                                    type="button"
                                    aria-label={`Abrir video de ${item.title}`}
                                    className={`pointer-events-none inline-flex items-center justify-center rounded-full bg-white text-zinc-950 shadow-[0_18px_34px_rgba(0,0,0,0.28)] ${
                                      isActive ? 'h-16 w-16' : 'h-14 w-14'
                                    } transition-transform duration-200 ease-out`}
                                  >
                                    <Play size={isActive ? 24 : 20} className="ml-1 group-hover:scale-105 transition-transform" fill="currentColor" />
                                  </button>
                                </div>

                                <div className="rounded-2xl border border-white/10 bg-black/30 px-4 py-3 backdrop-blur-md">
                                  <p className="text-[10px] uppercase tracking-[0.16em] text-white/55">{t('portfolio.resultLabel')}</p>
                                  <p className="mt-1 text-lg font-bold text-white" itemProp="description">{t(resultKeyMap[baseId] || 'portfolio.items.' + baseId + '_result')}</p>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(portfolioSchema),
          }}
        />
        <Dialog open={!!activeMedia} onOpenChange={handleDialogOpenChange}>
          <DialogContent className="w-full max-w-5xl p-0 sm:p-0" aria-describedby="video-description">
            <DialogTitle className="sr-only">Video</DialogTitle>
            <DialogDescription id="video-description" className="sr-only">Reproductor de video</DialogDescription>
            {activeMedia && (
              <div className="w-full max-h-[90vh] overflow-hidden rounded-xl bg-black/95 dark:bg-zinc-900/95 text-white flex items-center justify-center">
                <video
                  src={activeMedia.url}
                  poster={activeMedia.thumbnailUrl || ''}
                  controls
                  autoPlay
                  playsInline
                  className="w-full h-[550px] md:h-[650px] object-contain"
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
};

export default PortfolioSection;
